import { RefreshCw } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Days } from "../../../lib/schedule-presets/art-metadata";
import { Button } from "../../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Checkbox } from "../../ui/checkbox";
import { dayLayoutSignature, useDayLayoutSyncStore } from "../../../stores/day-layout-sync-store";
import { useScheduleStore } from "../../../stores/schedule-store";

function copyDayLayout(days: Days, sourceKey: keyof Days, targetKeys: (keyof Days)[]): Days {
  const source = days[sourceKey];
  const updates = targetKeys.map((targetKey) => {
    const target = days[targetKey];
    const copiedLayout = {
      ...structuredClone(source),
      origin: [...target.origin],
    };
    return [targetKey, copiedLayout] as const;
  });
  return { ...days, ...Object.fromEntries(updates) };
}

export function ApplyDayLayoutControl({
  days,
  dayKey,
  labels,
  onChange,
}: {
  days: Days;
  dayKey: keyof Days;
  labels: Record<string, string>;
  onChange: (days: Days) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState<(keyof Days)[]>([]);
  const id = useId();
  const presetId = useScheduleStore(state => state.preset.id);
  const sync = useDayLayoutSyncStore();
  const otherDayKeys = Object.keys(days).filter(key => key !== dayKey) as (keyof Days)[];
  const sourceSignature = dayLayoutSignature(days[dayKey]);
  const rows = otherDayKeys.map(key => {
    const signature = dayLayoutSignature(days[key]);
    const identical = signature === sourceSignature;
    const previousMatch = sync.presetId === presetId ? sync.matches[`${dayKey}:${key}`] : undefined;
    const pending = !identical && previousMatch === signature;
    return { key, identical, pending };
  });
  const toggleOpen = (next: boolean) => {
    if (next) setChecked(rows.filter(row => row.identical || row.pending).map(row => row.key));
    setOpen(next);
  };
  return (
    <Popover open={open} onOpenChange={toggleOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2 text-xs" title={t("metadata.layoutSync.title", { day: labels[dayKey] })}>
          <RefreshCw className="size-3.5" />{t("metadata.layoutSync.trigger")}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-3 p-3">
        <div className="space-y-1">
          <p className="text-xs font-semibold">{t("metadata.layoutSync.title", { day: labels[dayKey] })}</p>
          <p className="text-xs text-muted-foreground">{t("metadata.layoutSync.help")}</p>
        </div>
        <div className="space-y-1">
          {rows.map(({ key, identical, pending }) => (
            <label key={key} htmlFor={`${id}-${key}`} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 hover:bg-neutral-50">
              <Checkbox id={`${id}-${key}`} checked={checked.includes(key)} onCheckedChange={selected => {
                setChecked(current => selected === true ? [...current, key] : current.filter(day => day !== key));
              }} />
              <span className="flex-1 text-xs">{labels[key] ?? key}</span>
              {identical && <span className="text-xs font-medium text-green-700">{t("metadata.layoutSync.identical")}</span>}
              {pending && <span className="text-xs font-medium text-amber-600" title={t("metadata.layoutSync.pendingHelp", { day: labels[dayKey] })}>{t("metadata.layoutSync.pending")}</span>}
            </label>
          ))}
        </div>
        <Button type="button" size="sm" className="w-full text-xs" disabled={checked.length === 0} onClick={() => {
          onChange(copyDayLayout(days, dayKey, checked));
        }}>
          {t("metadata.layoutSync.apply", { count: checked.length })}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
